import {
  supabase,
} from "../supabaseClient";

import {
  API_BASE_URL,
} from "../config/apiBase";


// =====================================================
// AI CAREER RISK / CAREER RESILIENCE API
//
// API_BASE_URL is controlled centrally by:
//
// src/config/apiBase.js
//
// LOCAL:
// http://localhost:8080
//
// PRODUCTION:
// https://api.reignitespirit.com
//
// IMPORTANT:
// This file contains NO hardcoded localhost fallback.
// =====================================================


console.log(
  "[AI Career Risk] API Base:",
  API_BASE_URL
);


// =====================================================
// GET SUPABASE ACCESS TOKEN
// =====================================================

async function getAccessToken() {
  const {
    data,
    error,
  } =
    await supabase.auth
      .getSession();


  if (error) {
    console.error(
      "[AI Career Risk] Supabase session error:",
      error
    );

    throw new Error(
      "Unable to verify your login session."
    );
  }


  const token =
    data?.session
      ?.access_token;


  console.log(
    "[AI Career Risk] Session exists:",
    !!data?.session
  );


  console.log(
    "[AI Career Risk] Access token exists:",
    !!token
  );


  if (!token) {
    throw new Error(
      "Your session has expired. Please login again."
    );
  }


  return token;
}


// =====================================================
// SAFE RESPONSE READER
// =====================================================

async function readResponseBody(
  response
) {
  const contentType =
    response.headers.get(
      "content-type"
    ) || "";


  // ---------------------------------------------------
  // JSON RESPONSE
  // ---------------------------------------------------

  if (
    contentType.includes(
      "json"
    )
  ) {
    return await response
      .json()
      .catch(
        () => null
      );
  }


  // ---------------------------------------------------
  // TEXT FALLBACK
  // ---------------------------------------------------

  const text =
    await response
      .text()
      .catch(
        () => ""
      );


  return text
    ? {
        detail:
          text,
      }
    : null;
}


// =====================================================
// FORMAT VALIDATION ERRORS
// =====================================================

function getValidationMessage(
  body
) {
  if (
    !body?.errors ||
    typeof body.errors !==
      "object"
  ) {
    return null;
  }


  return Object.entries(
    body.errors
  )
    .flatMap(
      (
        [
          field,
          errors,
        ]
      ) =>
        (
          Array.isArray(
            errors
          )
            ? errors
            : [
                errors,
              ]
        ).map(
          (
            message
          ) =>
            `${field}: ${message}`
        )
    )
    .join(
      " "
    );
}


// =====================================================
// ANALYZE CAREER RESILIENCE
//
// POST
// /api/ai/career-resilience-analysis
// =====================================================

export async function analyzeCareerResilience(
  payload
) {
  // ---------------------------------------------------
  // Validate request
  // ---------------------------------------------------

  if (
    !payload ||
    typeof payload !==
      "object"
  ) {
    throw new Error(
      "Career assessment information is required."
    );
  }


  const controller =
    new AbortController();


  const timeoutId =
    window.setTimeout(
      () =>
        controller.abort(),
      120000
    );


  try {
    // =================================================
    // 1. GET CURRENT SUPABASE JWT
    // =================================================

    const token =
      await getAccessToken();


    // =================================================
    // 2. BUILD API URL
    //
    // Production:
    // https://api.reignitespirit.com/...
    //
    // Local:
    // http://localhost:8080/...
    //
    // Determined centrally by API_BASE_URL.
    // =================================================

    const url =
      `${API_BASE_URL}/api/ai/career-resilience-analysis`;


    console.log(
      "[AI Career Risk] POST:",
      url
    );


    // -------------------------------------------------
    // Do not log sensitive payload details unnecessarily
    // in production.
    // -------------------------------------------------

    if (
      process.env.NODE_ENV !==
      "production"
    ) {
      console.log(
        "[AI Career Risk] Payload:",
        payload
      );
    }


    // DO NOT log actual JWT token.
    console.log(
      "[AI Career Risk] Authorization attached:",
      !!token
    );


    // =================================================
    // 3. CALL BACKEND WITH BEARER TOKEN
    // =================================================

    const response =
      await fetch(
        url,
        {
          method:
            "POST",

          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body:
            JSON.stringify(
              payload
            ),

          signal:
            controller.signal,
        }
      );


    // =================================================
    // 4. READ RESPONSE
    // =================================================

    const body =
      await readResponseBody(
        response
      );


    console.log(
      "[AI Career Risk] Response status:",
      response.status
    );


    // Avoid dumping complete API responses in production.
    if (
      process.env.NODE_ENV !==
      "production"
    ) {
      console.log(
        "[AI Career Risk] Response:",
        body
      );
    }


    // =================================================
    // 5. HANDLE API ERRORS
    // =================================================

    if (
      !response.ok
    ) {
      console.error(
        "[AI Career Risk] API error:",
        {
          status:
            response.status,

          statusText:
            response.statusText,

          body:
            process.env.NODE_ENV !==
            "production"
              ? body
              : undefined,
        }
      );


      // -----------------------------------------------
      // 400 BAD REQUEST
      // -----------------------------------------------

      if (
        response.status ===
        400
      ) {
        const validationMessage =
          getValidationMessage(
            body
          );


        throw new Error(
          validationMessage ||
          body?.detail ||
          body?.message ||
          body?.title ||
          "Please check the information entered and try again."
        );
      }


      // -----------------------------------------------
      // 401 UNAUTHORIZED
      // -----------------------------------------------

      if (
        response.status ===
        401
      ) {
        throw new Error(
          "Your login session is no longer valid. Please log out and log in again."
        );
      }


      // -----------------------------------------------
      // 403 FORBIDDEN
      // -----------------------------------------------

      if (
        response.status ===
        403
      ) {
        throw new Error(
          "You are not authorized to use this assessment."
        );
      }


      // -----------------------------------------------
      // 404 NOT FOUND
      // -----------------------------------------------

      if (
        response.status ===
        404
      ) {
        throw new Error(
          "AI Career Risk API endpoint was not found."
        );
      }


      // -----------------------------------------------
      // 429 RATE LIMIT
      // -----------------------------------------------

      if (
        response.status ===
        429
      ) {
        throw new Error(
          "You have reached the AI analysis limit. Please try again later."
        );
      }


      // -----------------------------------------------
      // SERVER ERROR
      // -----------------------------------------------

      if (
        response.status >=
        500
      ) {
        throw new Error(
          body?.detail ||
          body?.message ||
          body?.title ||
          "Career analysis service is temporarily unavailable."
        );
      }


      // -----------------------------------------------
      // GENERIC ERROR
      // -----------------------------------------------

      throw new Error(
        body?.detail ||
        body?.message ||
        body?.title ||
        `Career analysis failed. (${response.status})`
      );
    }


    // =================================================
    // 6. SUCCESS
    // =================================================

    return body;
  }
  catch (
    error
  ) {
    // =================================================
    // REQUEST TIMEOUT
    // =================================================

    if (
      error?.name ===
      "AbortError"
    ) {
      throw new Error(
        "The analysis took too long. Please try again."
      );
    }


    // =================================================
    // NETWORK FAILURE
    // =================================================

    if (
      error instanceof
        TypeError &&
      error.message ===
        "Failed to fetch"
    ) {
      console.error(
        "[AI Career Risk] Network connection failed:",
        error
      );

      throw new Error(
        "Unable to connect to the Career Resilience service."
      );
    }


    console.error(
      "[AI Career Risk] Request failed:",
      error
    );


    throw error;
  }
  finally {
    window.clearTimeout(
      timeoutId
    );
  }
}


// =====================================================
// OPTIONAL EXPORT
//
// Existing files may already import API_BASE.
// Keep compatibility without recreating localhost logic.
// =====================================================

export const API_BASE =
  API_BASE_URL;